import {
  ApplicationAssigned,
  ApplicationUpdated,
  CalendarExpired,
  EmploymentVerified,
  EnquirerUpdated,
  LeasedPropertiesAutoArchived,
  MinStandardsConfirmed,
  ReferenceAdded,
  ReferenceArchived,
  ReferenceDeclined,
  ReferenceReassigned,
  ReferenceUnarchived,
  ViewingCancelled,
  ViewingCreated,
  ViewingMerged,
  ViewingUpdated,
} from 'app/shared_components/activity_feed/activity_feed.const'
import { urlTo } from 'app/sm/helpers'

const getFieldDisplayName = (fieldName) => {
  const fieldDisplayNames = {
    first_name: 'First Name',
    last_name: 'Last Name',
    email: 'Email',
    contact_number: 'Phone Number',
    message: 'Message',
    bedrooms: 'Bedrooms',
    rent: 'Rent',
    postcode: 'Postcode',
  }

  return (
    fieldDisplayNames[fieldName] ||
    fieldName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  )
}

export const getActivityLabel = (activity, { openTab }) => {
  switch (activity.type) {
    case ApplicationAssigned: {
      return `Application assigned to ${
        openTab.key === 'team' ? activity.data?.notified_name : 'you'
      }`
    }
    case ApplicationUpdated: {
      return `Application updated`
    }
    case ReferenceDeclined: {
      return 'Reference Declined'
    }
    case ReferenceAdded: {
      return 'Reference Added'
    }
    case EmploymentVerified: {
      return 'Employment Verified'
    }
    case LeasedPropertiesAutoArchived: {
      return 'Auto-archived leased properties'
    }
    case ReferenceArchived: {
      return 'Reference Archived'
    }
    case ReferenceUnarchived: {
      return 'Reference Unarchived'
    }
    case ReferenceReassigned: {
      return `Reference re-assigned to ${
        openTab.key === 'team' ? activity.data?.notified_name : 'you'
      }`
    }
    case CalendarExpired: {
      return `Calendar Sync expired: ${activity.data?.calendar_platform} calendar needs to be reconnected`
    }
    case ViewingCreated:
      return `Created viewing ${activity.data?.viewing_datetime || ''} for ${
        activity.data?.property_name
      }`
    case ViewingCancelled:
      return `Cancelled viewing ${activity.data?.viewing_datetime || ''} for ${
        activity.data?.property_name
      }`
    case ViewingUpdated:
      return `Updated viewing ${activity.data?.viewing_datetime || ''} for ${
        activity.data?.property_name
      }`
    case ViewingMerged:
      return `Merged viewing ${activity.data?.viewing_datetime || ''} for ${
        activity.data?.property_name
      } with ${activity.data?.cancelled_viewing_datetime || ''} for ${
        activity.data?.property_name
      }`
    case EnquirerUpdated: {
      const enquirerName = activity.data?.enquirer_name || 'an enquirer'
      const changedFields = activity.data?.changed_fields

      if (changedFields && Object.keys(changedFields).length > 0) {
        const fieldNames = Object.keys(changedFields)
        const fieldUpdates = fieldNames
          .map((fieldName) => {
            const field = changedFields[fieldName]
            const oldValue = field.old_value || ''
            const newValue = field.new_value || ''

            const displayFieldName = getFieldDisplayName(fieldName)

            return `${displayFieldName}: "${oldValue}" → "${newValue}"`
          })
          .join(', ')

        return `Enquirer updated for ${enquirerName} (${fieldUpdates})`
      }

      return `Enquirer details updated for ${enquirerName}`
    }

    case MinStandardsConfirmed: {
      return 'Published live property and confirmed Consumer Affairs Victoria Minimum Standards are met'
    }
  }
}

export const getActivityNavigationURL = (activity, { currentTeam }) => {
  const { data = {} } = activity

  switch (activity.type) {
    case ApplicationUpdated:
    case ApplicationAssigned:
    case EmploymentVerified: {
      if (
        !data?.application_guid ||
        !data?.primary_applicant_guid ||
        !data?.property_guid
      ) {
        return ''
      }

      return `/sm/property/${data.property_guid}/applications/${data.application_guid}/applicant/${data.primary_applicant_guid}`
    }
    case ReferenceDeclined:
    case ReferenceAdded: {
      if (!currentTeam) return ''

      return `/teams/${currentTeam.slug}/rental-references`
    }
    case ReferenceArchived:
    case ReferenceUnarchived:
    case ReferenceReassigned: {
      if (!currentTeam) return ''
      return `/teams/${currentTeam.slug}/rental-references${
        data.property_name && `?q=${data.property_name}`
      }`
    }
    case CalendarExpired: {
      return urlTo('teamCalendarSync', { teamSlug: currentTeam.slug })
    }
    case EnquirerUpdated: {
      if (!data?.enquirer_guid) {
        return ''
      }

      return `${urlTo('teamMessages', {
        teamSlug: currentTeam.slug,
      })}?messageGUID=${data?.enquirer_guid}`
    }
    case MinStandardsConfirmed: {
      if (!data?.property_guid || !currentTeam) {
        return ''
      }

      return `${urlTo('prospectSummary', {
        teamSlug: currentTeam.slug,
      })}?property=${data.property_guid}&stage=Application`
    }
    default:
      return ''
  }
}
